<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Student Class Enrollment Management
                </h1>
                <div class="flex space-x-3">
                    <button
                        @click="openImportModal"
                        class="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                        Import
                    </button>
                    <button
                        @click="openBulkModal"
                        class="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                        Bulk Enrollment
                    </button>
                    <button
                        @click="openModal"
                        class="inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                    >
                        <Plus class="w-4 h-4 mr-1" />
                        Add Enrollment
                    </button>
                </div>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Search
                        </label>
                        <input
                            v-model="searchQuery"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search by student name, class name, or semester..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Student Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.student_id"
                            :options="studentOptions"
                            label="Student"
                            placeholder="All Students"
                            :option-label="getStudentLabel"
                            option-value="id"
                            @update:model-value="fetchStudentClasses"
                        />
                    </div>

                    <!-- Class Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.class_id"
                            :options="classOptions"
                            label="Class"
                            placeholder="All Classes"
                            :option-label="getClassLabel"
                            option-value="id"
                            @update:model-value="fetchStudentClasses"
                        />
                    </div>

                    <!-- Semester Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.semester_id"
                            :options="semesterOptions"
                            label="Semester"
                            placeholder="All Semesters"
                            :option-label="getSemesterLabel"
                            option-value="id"
                            @update:model-value="fetchStudentClasses"
                        />
                    </div>

                    <!-- Status Filter -->
                    <StatusFilter
                        v-model="filters.is_active"
                        @change="fetchStudentClasses"
                    />
                    <!-- Page Length Selector -->
                    <PageLengthSelector
                        v-model="studentClassStore.perPage"
                        @update:model-value="handlePerPageChange"
                    />
                </div>
                <div class="flex justify-between items-center mt-4">
                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        Reset Filters
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div
                v-if="studentClassStore.loading"
                class="flex justify-center items-center py-12"
            >
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="studentClassStore.error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ studentClassStore.error }}
                        </p>
                        <button
                            @click="studentClassStore.clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!studentClassStore.loading && !studentClassStore.error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Student
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Class
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Semester
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Enrollment Date
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Duration
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="(
                                    studentClass, index
                                ) in studentClassStore.studentClasses"
                                :key="
                                    typeof studentClass === 'object'
                                        ? studentClass.id
                                        : index
                                "
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900 dark:text-white"
                                    >
                                        {{
                                            studentClass?.student?.user?.name ||
                                            "-"
                                        }}
                                        <div
                                            v-if="
                                                studentClass?.student
                                                    ?.student_id
                                            "
                                            class="text-xs text-gray-500 dark:text-gray-400"
                                        >
                                            ({{
                                                studentClass?.student
                                                    ?.student_id
                                            }})
                                        </div>
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ studentClass.class?.grade_level || "-" }}
                                    -
                                    {{ studentClass.class?.name || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{
                                        studentClass.semester?.name &&
                                        studentClass.semester?.academic_year
                                            ?.name
                                            ? `${studentClass.semester.name} - ${studentClass.semester.academic_year.name}`
                                            : studentClass.semester?.name || "-"
                                    }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{
                                        formatDate(studentClass.enrollment_date)
                                    }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                                >
                                    {{ studentClass.duration || "-" }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        v-if="studentClass.is_active"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    >
                                        Active
                                    </span>
                                    <span
                                        v-else
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                    >
                                        Inactive
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <button
                                        @click="editStudentClass(studentClass)"
                                        title="Edit Enrollment"
                                        class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mr-2"
                                    >
                                        <Pencil class="w-4 h-4" />
                                    </button>
                                    <button
                                        @click="confirmDelete(studentClass)"
                                        title="Delete Enrollment"
                                        class="inline-flex items-center justify-center p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Trash2 class="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty State -->
                <div
                    v-if="studentClassStore.studentClasses.length === 0"
                    class="text-center py-12"
                >
                    <Users class="mx-auto h-12 w-12 text-gray-400" />
                    <h3
                        class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                        No student class enrollments found
                    </h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Get started by adding a new student class enrollment.
                    </p>
                    <div class="mt-6">
                        <button
                            @click="openModal"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus class="w-4 h-4 mr-1" />
                            Add Enrollment
                        </button>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div
                v-if="
                    (studentClassStore.meta?.total || 0) >
                    (studentClassStore.meta?.per_page || 10)
                "
                class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6"
            >
                <div class="flex-1 flex justify-between sm:hidden">
                    <button
                        @click="previousPage"
                        :disabled="studentClassStore.meta?.current_page === 1"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Previous
                    </button>
                    <button
                        @click="nextPage"
                        :disabled="
                            studentClassStore.meta?.current_page ===
                            studentClassStore.meta?.last_page
                        "
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Next
                    </button>
                </div>
                <div
                    class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                >
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{
                                studentClassStore.meta?.from ?? 0
                            }}</span>
                            to
                            <span class="font-medium">{{
                                studentClassStore.meta?.to ?? 0
                            }}</span>
                            of
                            <span class="font-medium">{{
                                studentClassStore.meta?.total ?? 0
                            }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav
                            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                @click="previousPage"
                                :disabled="
                                    studentClassStore.meta?.current_page === 1
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Previous
                            </button>
                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                @click="
                                    typeof page === 'number'
                                        ? goToPage(page)
                                        : null
                                "
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page ===
                                    studentClassStore.meta?.current_page
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300',
                                    typeof page === 'string'
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer',
                                ]"
                                :disabled="typeof page === 'string'"
                            >
                                {{ page }}
                            </button>
                            <button
                                @click="nextPage"
                                :disabled="
                                    studentClassStore.meta?.current_page ===
                                    studentClassStore.meta?.last_page
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- StudentClass Modal -->
            <StudentClassModal
                :showModal="showModal"
                :studentClass="selectedStudentClass"
                :isEditing="isEditing"
                @close="closeModal"
                @saved="handleSaved"
            />

            <!-- Bulk StudentClass Modal -->
            <BulkStudentClassModal
                :showModal="showBulkModal"
                @close="closeBulkModal"
                @saved="handleBulkSaved"
            />

            <!-- Import StudentClass Modal -->
            <StudentClassImportModal
                v-if="showImportModal"
                @close="closeImportModal"
                @success="handleImportSuccess"
            />

            <!-- Delete Confirmation Modal -->
            <DeleteConfirmationModal
                v-if="showDeleteModal"
                :show="showDeleteModal"
                :title="deleteModalTitle"
                :message="deleteModalMessage"
                @confirm="handleDelete"
                @close="closeDeleteModal"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useStudentClassStore } from "@/stores/studentClassStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import StatusFilter from "@/components/StatusFilter.vue";
import PageLengthSelector from "@/components/PageLengthSelector.vue";
import StudentClassModal from "@/components/StudentClassModal.vue";
import BulkStudentClassModal from "@/components/BulkStudentClassModal.vue";
import StudentClassImportModal from "@/components/StudentClassImportModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import NotificationService from "@/utils/notifications";
import studentApi from "@/api/student";
import classSchoollApi from "@/api/classSchooll";
import semesterApi from "@/api/semester";
import type { StudentClass } from "@/api/studentClass";
import type { Student } from "@/api/student";
import type { ClassSchooll } from "@/api/classSchooll";
import type { Semester } from "@/api/semester";
import { Plus, RotateCcw, Pencil, Trash2, Users } from "@lucide/vue";

const studentClassStore = useStudentClassStore();
// State
const searchQuery = ref("");
const filters = ref({
    student_id: "",
    class_id: "",
    semester_id: "",
    is_active: undefined as boolean | undefined,
});
const showModal = ref(false);
const showBulkModal = ref(false);
const showImportModal = ref(false);
const showDeleteModal = ref(false);
const selectedStudentClass = ref<StudentClass | null>(null);
const isEditing = ref(false);
const studentToDelete = ref<StudentClass | null>(null);
// Types for options with "Select All" option
type StudentOption =
    | Student
    | { id: ""; user: { name: string }; student_id: string };
type ClassOption = ClassSchooll | { id: ""; grade_level: string; name: string };
type SemesterOption =
    | Semester
    | { id: ""; name: string; academic_year: { name: string } };

const studentOptions = ref<StudentOption[]>([]);
const classOptions = ref<ClassOption[]>([]);
const semesterOptions = ref<SemesterOption[]>([]);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this student class enrollment? This action cannot be undone.",
);

// Computed

const visiblePages = computed(() => {
    const current = studentClassStore.meta?.current_page || 1;
    const last = studentClassStore.meta?.last_page || 1;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    if (current - delta > 2) {
        rangeWithDots.push(1, "...");
    } else {
        rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < last - 1) {
        rangeWithDots.push("...", last);
    } else if (last > 1) {
        rangeWithDots.push(last);
    }

    return rangeWithDots;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.student_id !== "" ||
        filters.value.class_id !== "" ||
        filters.value.semester_id !== "" ||
        filters.value.is_active !== undefined ||
        studentClassStore.page !== 1 ||
        studentClassStore.perPage !== 15
    );
});

// Methods
const handlePerPageChange = (value: number) => {
    studentClassStore.perPage = value;
    studentClassStore.page = 1;
    fetchStudentClasses();
};

const fetchStudentClasses = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await studentClassStore.fetchStudentClasses(searchFilters);
};

const fetchOptions = async () => {
    try {
        const [studentsResponse, classesResponse, semestersResponse] =
            await Promise.all([
                studentApi.getAll({ per_page: 1000 }),
                classSchoollApi.getAll({ per_page: 1000 }),
                semesterApi.getSemesters({ per_page: 1000 }),
            ]);
        // Add "Select All" option as the first element
        studentOptions.value = [
            { id: "", user: { name: "All Students" }, student_id: "" },
            ...(studentsResponse.data?.items || []),
        ];
        classOptions.value = [
            { id: "", grade_level: "", name: "All Classes" },
            ...(classesResponse.data?.items || []),
        ];
        semesterOptions.value = [
            { id: "", name: "All Semesters", academic_year: { name: "" } },
            ...(semestersResponse.data.items || []),
        ];
    } catch (error) {
        console.error("Failed to fetch options:", error);
    }
};

const getStudentLabel = (student: StudentOption): string => {
    if (student.id === "") return student.user.name;
    return `${student.user?.name} (${student.student_id})`;
};

const getClassLabel = (classOption: ClassOption): string => {
    if (classOption.id === "") return classOption.name;
    return `${classOption.grade_level} - ${classOption.name}`;
};

const getSemesterLabel = (semester: SemesterOption): string => {
    if (semester.id === "") return semester.name;
    return `${semester.name} (${semester.academic_year?.name})`;
};

const debouncedSearch = useDebounce(fetchStudentClasses, 300);

const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
};

const openModal = () => {
    selectedStudentClass.value = null;
    isEditing.value = false;
    showModal.value = true;
};

const openBulkModal = () => {
    showBulkModal.value = true;
};

const editStudentClass = (studentClass: StudentClass) => {
    selectedStudentClass.value = studentClass;
    isEditing.value = true;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedStudentClass.value = null;
    isEditing.value = false;
};

const closeBulkModal = () => {
    showBulkModal.value = false;
};

const openImportModal = () => {
    showImportModal.value = true;
};

const closeImportModal = () => {
    showImportModal.value = false;
};

const handleImportSuccess = () => {
    showNotification("Student classes imported successfully", "success");
    fetchStudentClasses();
};

const confirmDelete = (studentClass: StudentClass) => {
    studentToDelete.value = studentClass;
    deleteModalTitle.value = "Delete Student Class Enrollment";
    deleteModalMessage.value = `Are you sure you want to delete the enrollment for "${studentClass.student?.user?.name}" in "${studentClass.class?.name}"? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    studentToDelete.value = null;
};

const handleSaved = () => {
    // Modal handles all API calls and errors
    // This handler just closes modal and refreshes data
    closeModal();
    fetchStudentClasses();
};

const handleBulkSaved = async (bulkData: any) => {
    try {
        // Check if the data is from import or bulk create
        if (Array.isArray(bulkData)) {
            // Import response - array of imported student classes
            const successMessage = `Successfully imported ${bulkData.length} student class enrollments`;
            showNotification(successMessage, "success");
            closeBulkModal();
        } else {
            // Bulk create response
            const result =
                await studentClassStore.bulkCreateStudentClasses(bulkData);

            if (result) {
                const successMessage = `Successfully enrolled ${result.success_count} out of ${result.total_count} students`;
                if (result.error_count > 0) {
                    showNotification(
                        `${successMessage}. ${result.error_count} students could not be enrolled.`,
                        "warning",
                    );
                } else {
                    showNotification(successMessage, "success");
                }
                closeBulkModal();
            } else {
                // Show error from store if bulk creation failed
                if (studentClassStore.error) {
                    showNotification(studentClassStore.error, "error");
                }
            }
        }
    } catch (error) {
        console.error("Failed to bulk enroll students:", error);
        showNotification("Failed to bulk enroll students", "error");
    }
};

const handleDelete = async () => {
    try {
        if (studentToDelete.value) {
            await studentClassStore.deleteStudentClass(
                studentToDelete.value.id,
            );
            closeDeleteModal();
        }
    } catch (error) {
        // Error handling is already in the store
        console.error("Failed to delete student class:", error);
    }
};

const previousPage = () => {
    if ((studentClassStore.meta?.current_page || 1) > 1) {
        studentClassStore.setPage(
            (studentClassStore.meta?.current_page || 1) - 1,
        );
        fetchStudentClasses();
    }
};

const nextPage = () => {
    if (
        (studentClassStore.meta?.current_page || 1) <
        (studentClassStore.meta?.last_page || 1)
    ) {
        studentClassStore.setPage(
            (studentClassStore.meta?.current_page || 1) + 1,
        );
        fetchStudentClasses();
    }
};

const goToPage = (page: number) => {
    studentClassStore.setPage(page);
    fetchStudentClasses();
};

const resetFilters = async () => {
    searchQuery.value = "";
    filters.value.student_id = "";
    filters.value.class_id = "";
    filters.value.semester_id = "";
    filters.value.is_active = undefined;
    await studentClassStore.resetFilters();
    fetchStudentClasses();
};

const showNotification = (
    message: string,
    type: "success" | "error" | "info" | "warning",
) => {
    switch (type) {
        case "success":
            NotificationService.success(message);
            break;
        case "error":
            NotificationService.error(message);
            break;
        case "info":
            NotificationService.info(message);
            break;
        case "warning":
            NotificationService.warning(message);
            break;
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchStudentClasses();
    fetchOptions();
});
</script>
